import { useState, type FormEvent } from 'react';
import { Key, Plus, Trash2, Copy, Check, AlertTriangle } from 'lucide-react';
import { useApiKeys, useGenerateApiKey, useRevokeApiKey, type GeneratedApiKey } from '../hooks/useApiKeys';
import { useAuth } from '../contexts/AuthContext';
import ConnectionGuide from '../components/ConnectionGuide';

export default function Settings() {
  const { user } = useAuth();
  const { data: apiKeys, isLoading } = useApiKeys();
  const generateKey = useGenerateApiKey();
  const revokeKey = useRevokeApiKey();

  const [showGenerate, setShowGenerate] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [generatedKey, setGeneratedKey] = useState<GeneratedApiKey | null>(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async (e: FormEvent) => {
    e.preventDefault();
    const result = await generateKey.mutateAsync(keyName);
    setGeneratedKey(result);
    setKeyName('');
    setShowGenerate(false);
  };

  const handleCopyKey = async () => {
    if (!generatedKey) return;
    await navigator.clipboard.writeText(generatedKey.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRevoke = (id: string, prefix: string) => {
    if (!confirm(`Revoke API key "${prefix}..."? This cannot be undone.`)) return;
    revokeKey.mutate(id);
  };

  return (
    <div className="p-8">
      <h1 className="mb-1 text-2xl font-bold text-slate-900">Settings</h1>
      <p className="mb-8 text-sm text-slate-500">Manage your API keys and account</p>

      <div className="max-w-2xl space-y-8">
        {/* API Keys */}
        <section>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">API Keys</h2>
            <button
              onClick={() => setShowGenerate(true)}
              className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              <Plus className="h-4 w-4" />
              Generate New Key
            </button>
          </div>

          {/* Generated key banner */}
          {generatedKey && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-medium text-amber-800">
                <AlertTriangle className="h-4 w-4" />
                Copy your API key now. It will not be shown again.
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-lg bg-white px-3 py-2 font-mono text-sm text-slate-900 border border-amber-200">
                  {generatedKey.key}
                </code>
                <button
                  onClick={handleCopyKey}
                  className="rounded-lg border border-amber-200 bg-white p-2 text-slate-600 hover:bg-slate-50"
                >
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
              <button
                onClick={() => setGeneratedKey(null)}
                className="mt-2 text-xs text-amber-600 hover:text-amber-800"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Generate modal */}
          {showGenerate && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
              <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-6 shadow-lg">
                <h3 className="mb-4 text-lg font-semibold text-slate-900">Generate API Key</h3>
                <form onSubmit={handleGenerate} className="space-y-4">
                  <div>
                    <label htmlFor="key-name" className="mb-1.5 block text-sm font-medium text-slate-700">
                      Key Name
                    </label>
                    <input
                      id="key-name"
                      type="text"
                      required
                      value={keyName}
                      onChange={(e) => setKeyName(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                      placeholder="e.g. Production, Development"
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowGenerate(false)}
                      className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={generateKey.isPending}
                      className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                    >
                      {generateKey.isPending ? 'Generating...' : 'Generate'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Key list */}
          <div className="rounded-xl border border-slate-200 bg-white">
            {isLoading ? (
              <div className="p-6 text-sm text-slate-400">Loading keys...</div>
            ) : !apiKeys?.length ? (
              <div className="p-6 text-center">
                <Key className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                <p className="text-sm text-slate-500">No API keys yet</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {apiKeys.map((key) => (
                  <div key={key.id} className="flex items-center justify-between px-6 py-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-medium text-slate-900">{key.prefix}...</code>
                        <span className="text-sm text-slate-500">{key.name}</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-400">
                        Created {new Date(key.createdAt).toLocaleDateString()}
                        {key.lastUsedAt && ` | Last used ${new Date(key.lastUsedAt).toLocaleDateString()}`}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRevoke(key.id, key.prefix)}
                      disabled={revokeKey.isPending}
                      className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Connection Guide */}
        <section>
          <h2 className="mb-4 text-lg font-semibold text-slate-900">Connection Guide</h2>
          <p className="mb-4 text-sm text-slate-500">
            Use these configuration snippets to connect your AI assistant to Aldan MCP.
          </p>
          <ConnectionGuide apiKey={generatedKey?.key} />
        </section>

        {/* Account */}
        <section>
          <h2 className="mb-4 text-lg font-semibold text-slate-900">Account</h2>
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="text-sm text-slate-600">
              <span className="font-medium">Email:</span> {user?.email}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
